import { BaseApiResponse } from '../../models/BaseApiResponse';
import { 
  BackendApiResponse, 
  BackendAuthResponse, 
  BackendAuthUser, 
  BackendVerifyTokenResponse, 
  BackendUser, 
  BackendDevice, 
  BackendEvent, 
  BackendEventType, 
  BackendNotification, 
  BackendRating, 
  BackendEventRatingStats, 
  BackendUserRatingStats, 
  BackendPaymentPreference, 
  BackendChatConfig, 
  BackendUserCountReport, 
  BackendHealthStatus, 
  BackendVersionInfo, 
  BackendPaginatedResponse,
  BackendMapper 
} from './backend-types';
import { 
  ValidationError,
  safeValidate,
  validateArrayData,
  validateAuthResponse,
  validateAuthUser,
  validateUser,
  validateEvent,
  validateNotification,
  validateRating
} from './validation-schemas';

// Production-ready Backend Mapper Implementation
export class BackendMapperImpl implements BackendMapper {
  
  // Generic mapping function with error handling
  private mapWithErrorHandling<T, U>(
    data: T,
    validator: (data: unknown) => U,
    entityName: string,
    transformer?: (data: U) => U
  ): U {
    try {
      const validated = safeValidate(data, entityName, validator);
      return transformer ? transformer(validated) : validated;
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error(`Validation error in ${entityName}:`, error.errors);
        throw error;
      }
      console.error(`Unexpected error mapping ${entityName}:`, error);
      throw new ValidationError(`Failed to map ${entityName}`, error);
    }
  }

  // Generic array mapping with error handling
  private mapArrayWithErrorHandling<T, U>(
    data: T[],
    validator: (data: unknown) => U,
    entityName: string,
    transformer?: (data: U) => U
  ): U[] {
    try {
      const validated = validateArrayData(data, entityName, validator);
      return transformer ? validated.map(transformer) : validated;
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error(`Validation error in ${entityName} array:`, error.errors);
        throw error;
      }
      console.error(`Unexpected error mapping ${entityName} array:`, error);
      throw new ValidationError(`Failed to map ${entityName} array`, error);
    }
  }

  // Auth mapping
  mapAuthResponse(response: BackendAuthResponse): BackendAuthResponse {
    return this.mapWithErrorHandling(response, validateAuthResponse, 'AuthResponse', (data) => ({
      ...data,
      // Transform expiration time to milliseconds if needed
      expiresIn: data.expiresIn * 1000,
    }));
  }

  mapAuthUser(response: BackendAuthUser): BackendAuthUser {
    return this.mapWithErrorHandling(response, validateAuthUser, 'AuthUser', (data) => ({
      ...data,
      // Ensure consistent boolean format
      banned: Boolean(data.banned),
    }));
  }

  mapVerifyTokenResponse(response: BackendVerifyTokenResponse): BackendVerifyTokenResponse {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid token verification response');
      }
      const d = data as any;
      if (
        typeof d.valid === 'boolean' &&
        typeof d.cognitoId === 'string' &&
        typeof d.email === 'string'
      ) {
        return d as BackendVerifyTokenResponse;
      }
      throw new Error('Invalid token verification structure');
    }, 'VerifyTokenResponse');
  }

  // User mapping
  mapUser(response: BackendUser): BackendUser {
    return this.mapWithErrorHandling(response, validateUser, 'User', (data) => ({
      ...data,
      // Normalize phone numbers
      userPhone: Number(data.userPhone),
      // Ensure dates are in ISO format (with error handling)
      birthDate: data.birthDate ? (new Date(data.birthDate).toISOString() || data.birthDate) : data.birthDate,
      createdAt: data.createdAt ? (new Date(data.createdAt).toISOString() || data.createdAt) : data.createdAt,
      updatedAt: data.updatedAt ? (new Date(data.updatedAt).toISOString() || data.updatedAt) : data.updatedAt,
    }));
  }

  mapUsers(response: BackendUser[]): BackendUser[] {
    return this.mapArrayWithErrorHandling(response, validateUser, 'User', (user) => this.mapUser(user));
  }

  mapDevice(response: BackendDevice): BackendDevice {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid device response');
      }
      const d = data as any;
      if (
        typeof d.deviceId === 'string' &&
        typeof d.deviceToken === 'string' &&
        typeof d.deviceInfo === 'object'
      ) {
        return d as BackendDevice;
      }
      throw new Error('Invalid device structure');
    }, 'Device');
  }

  // Event mapping
  mapEvent(response: BackendEvent): BackendEvent {
    return this.mapWithErrorHandling(response, validateEvent, 'Event', (data) => ({
      ...data,
      // Ensure dates are in ISO format
      startDate: new Date(data.startDate).toISOString(),
      // Normalize numeric fields
      amount: Number(data.amount),
      guests: Number(data.guests),
      inscription: Number(data.inscription),
      currentParticipants: Number(data.currentParticipants),
      price: data.price ? Number(data.price) : undefined,
    }));
  }

  mapEvents(response: BackendEvent[]): BackendEvent[] {
    return this.mapArrayWithErrorHandling(response, validateEvent, 'Event', (event) => this.mapEvent(event));
  }

  mapEventType(response: BackendEventType): BackendEventType {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid event type response');
      }
      const d = data as any;
      if (
        typeof d.id === 'number' &&
        typeof d.name === 'string' &&
        typeof d.subType === 'object' &&
        typeof d.subType.id === 'number' &&
        typeof d.subType.name === 'string'
      ) {
        return d as BackendEventType;
      }
      throw new Error('Invalid event type structure');
    }, 'EventType');
  }

  // Notification mapping
  mapNotification(response: BackendNotification): BackendNotification {
    return this.mapWithErrorHandling(response, validateNotification, 'Notification', (data) => ({
      ...data,
      // Ensure date is in ISO format
      createdAt: new Date(data.createdAt).toISOString(),
    }));
  }

  mapNotifications(response: BackendNotification[]): BackendNotification[] {
    return this.mapArrayWithErrorHandling(response, validateNotification, 'Notification', (notification) => this.mapNotification(notification));
  }

  // Rating mapping
  mapRating(response: BackendRating): BackendRating {
    return this.mapWithErrorHandling(response, validateRating, 'Rating', (data) => ({
      ...data,
      // Normalize rating to 1-5 range
      rating: Math.max(1, Math.min(5, Number(data.rating))),
      // Ensure date is in ISO format
      createdAt: new Date(data.createdAt).toISOString(),
    }));
  }

  mapRatings(response: BackendRating[]): BackendRating[] {
    return this.mapArrayWithErrorHandling(response, validateRating, 'Rating', (rating) => this.mapRating(rating));
  }

  mapEventRatingStats(response: BackendEventRatingStats): BackendEventRatingStats {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid event rating stats response');
      }
      const d = data as any;
      if (
        typeof d.eventId === 'number' &&
        typeof d.averageRating === 'number' &&
        typeof d.totalRatings === 'number' &&
        typeof d.ratingDistribution === 'object'
      ) {
        return d as BackendEventRatingStats;
      }
      throw new Error('Invalid event rating stats structure');
    }, 'EventRatingStats');
  }

  mapUserRatingStats(response: BackendUserRatingStats): BackendUserRatingStats {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid user rating stats response');
      }
      const d = data as any;
      if (
        typeof d.userId === 'number' &&
        typeof d.averageRating === 'number' &&
        typeof d.totalRatings === 'number' &&
        typeof d.totalHostRatings === 'number' &&
        typeof d.averageHostRating === 'number'
      ) {
        return d as BackendUserRatingStats;
      }
      throw new Error('Invalid user rating stats structure');
    }, 'UserRatingStats');
  }

  // Payment mapping
  mapPaymentPreference(response: BackendPaymentPreference): BackendPaymentPreference {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid payment preference response');
      }
      const d = data as any;
      if (
        typeof d.preferenceId === 'string' &&
        typeof d.initPoint === 'string' &&
        typeof d.sandboxInitPoint === 'string'
      ) {
        return d as BackendPaymentPreference;
      }
      throw new Error('Invalid payment preference structure');
    }, 'PaymentPreference');
  }

  // Messaging mapping
  mapChatConfig(response: BackendChatConfig): BackendChatConfig {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid chat config response');
      }
      const d = data as any;
      if (
        typeof d.appId === 'string' &&
        typeof d.region === 'string' &&
        typeof d.authKey === 'string'
      ) {
        return d as BackendChatConfig;
      }
      throw new Error('Invalid chat config structure');
    }, 'ChatConfig');
  }

  // Report mapping
  mapUserCountReport(response: BackendUserCountReport): BackendUserCountReport {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid user count report response');
      }
      const d = data as any;
      if (typeof d.totalUsers === 'number') {
        return d as BackendUserCountReport;
      }
      throw new Error('Invalid user count report structure');
    }, 'UserCountReport');
  }

  // System mapping
  mapHealthStatus(response: BackendHealthStatus): BackendHealthStatus {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid health status response');
      }
      const d = data as any;
      if (
        (d.status === 'healthy' || d.status === 'unhealthy') &&
        typeof d.timestamp === 'string' &&
        typeof d.uptime === 'number'
      ) {
        return d as BackendHealthStatus;
      }
      throw new Error('Invalid health status structure');
    }, 'HealthStatus');
  }

  mapVersionInfo(response: BackendVersionInfo): BackendVersionInfo {
    return this.mapWithErrorHandling(response, (data) => {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid version info response');
      }
      const d = data as any;
      if (
        typeof d.version === 'string' &&
        typeof d.buildTime === 'string' &&
        (d.gitCommit === undefined || typeof d.gitCommit === 'string')
      ) {
        return d as BackendVersionInfo;
      }
      throw new Error('Invalid version info structure');
    }, 'VersionInfo');
  }

  // Generic mapping
  mapApiResponse<T>(response: BackendApiResponse<T>): BaseApiResponse<T> {
    try {
      if (typeof response !== 'object' || response === null) {
        throw new Error('Invalid API response format');
      }

      const baseResponse: BaseApiResponse<T> = {
        success: Boolean(response.success),
        message: response.error?.message,
        data: response.data,
      };

      return baseResponse;
    } catch (error) {
      console.error('Error mapping API response:', error);
      throw new ValidationError('Failed to map API response', error);
    }
  }

  mapPaginatedResponse<T>(response: BackendPaginatedResponse<T>): BaseApiResponse<{
    items: T[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      if (
        !response ||
        typeof response !== 'object' ||
        !response.success ||
        !response.data ||
        !response.data.items ||
        !response.data.pagination
      ) {
        throw new Error('Invalid paginated response format');
      }

      return {
        success: true,
        data: {
          items: response.data.items,
          pagination: {
            page: Number(response.data.pagination.page),
            limit: Number(response.data.pagination.limit),
            total: Number(response.data.pagination.total),
            totalPages: Number(response.data.pagination.totalPages),
          },
        },
      };
    } catch (error) {
      console.error('Error mapping paginated response:', error);
      throw new ValidationError('Failed to map paginated response', error);
    }
  }
}

// Singleton instance for global use
export const backendMapper = new BackendMapperImpl();

// Utility function for mapping with optional error handling
export function mapBackendResponse<T, U>(
  response: T,
  mapperFn: (data: T, mapper: BackendMapperImpl) => U,
  options: {
    fallback?: U;
    logErrors?: boolean;
  } = { logErrors: true }
): U {
  try {
    return mapperFn(response, backendMapper);
  } catch (error) {
    if (options.logErrors) {
      console.error('Backend mapping error:', error);
    }
    
    if (options.fallback !== undefined) {
      return options.fallback;
    }
    
    throw error;
  }
}